import React from "react";
import styles from "./W3Card.module.css";
import W3Timer from "./W3Timer";
import { toKeyYYYYMMDD, toHHMM, todayKeyBR } from "../../../utils/datesBR";

const ADERENCIA_LIMIAR = 0.95;

const normStr = (v) => (v == null ? "" : String(v));

export const STATUS_LABEL = {
  em_andamento: "Não Iniciado",
  primeira_previa: "Primeira Prévia",
  segunda_previa: "Segunda Prévia",
  executado: "Executado",
  reprogramar: "Reprogramar",
  finalizado: "Finalizado",
};

export function getStatusKey(p) {
  const wf = normStr(p?.status_workflow || p?.status_wf3).trim().toLowerCase();
  const fin = normStr(p?.status_finalizacao).trim().toLowerCase();

  if (/finaliz/.test(fin) || /finaliz/.test(wf)) return "finalizado";
  if (/reprog/.test(fin) || /reprog/.test(wf)) return "reprogramar";
  if (/execut/.test(fin) || /execut/.test(wf)) return "executado";

  if (/segunda/.test(fin) || fin === "2" || fin === "segunda_previa") {
    return "segunda_previa";
  }
  if (/primeira/.test(fin) || fin === "1" || fin === "primeira_previa") {
    return "primeira_previa";
  }

  const segundaTxt = normStr(p?.segunda_previa).trim();
  const primeiraTxt = normStr(p?.primeira_previa).trim();
  if (segundaTxt) return "segunda_previa";
  if (primeiraTxt) return "primeira_previa";

  if (/andamento/.test(fin) || /andamento/.test(wf)) return "em_andamento";

  return "em_andamento";
}

export const STATUS_ORDER = {
  em_andamento: 0,
  primeira_previa: 1,
  segunda_previa: 2,
  executado: 3,
  reprogramar: 3,
  finalizado: 4,
};

export function statusOrderIndex(p) {
  return STATUS_ORDER[getStatusKey(p)] ?? 99;
}

function hasRealStartValue(v) {
  const s = normStr(v).trim();
  if (!s || s === "—" || s === "-") return false;
  if (/^0{1,2}:0{2}(?::0{2})?$/.test(s)) return false;
  return true;
}

function toMinutes(any) {
  const m = normStr(any)
    .trim()
    .match(/^(\d{1,3}):([0-5]\d)(?::([0-5]\d))?$/);
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

function sumIntervalsMinutes(obj) {
  const periods = [
    ["primeiro_inicio", "primeiro_fim"],
    ["segundo_inicio", "segundo_fim"],
    ["terceiro_inicio", "terceiro_fim"],
  ];
  let total = 0;
  for (const [startKey, endKey] of periods) {
    const start = toMinutes(obj?.[startKey]);
    const end = toMinutes(obj?.[endKey]);
    if (start != null && end != null && end > start) total += end - start;
  }
  return total;
}

function parsePtBRNumber(text) {
  if (text == null || text === "") return null;
  const normalized = normStr(text)
    .replace(/\s+/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

function toNumberOrNull(v) {
  if (v == null || v === "") return null;
  if (typeof v === "string" && /[,.]/.test(v)) return parsePtBRNumber(v);
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function safeRatio(real, previsto) {
  if (real == null || previsto == null) return null;
  const num = Number(real);
  const den = Number(previsto);
  if (!Number.isFinite(num) || !Number.isFinite(den) || den <= 0) return null;
  return num / den;
}

function getAderenciaStatus(
  tempoPrevistoMinutos,
  tempoRealizadoMinutos,
  quantidadePrevista,
  quantidadeRealizada
) {
  const ratioTempo = safeRatio(tempoRealizadoMinutos, tempoPrevistoMinutos);
  const ratioQtd = safeRatio(quantidadeRealizada, quantidadePrevista);

  const pctTempo = ratioTempo == null ? null : Math.max(0, ratioTempo * 100);
  const pctQtd = ratioQtd == null ? null : Math.max(0, ratioQtd * 100);

  const tempoOk = ratioTempo == null ? true : ratioTempo >= ADERENCIA_LIMIAR;
  const qtdOk = ratioQtd == null ? true : ratioQtd >= ADERENCIA_LIMIAR;

  const hasTempo = pctTempo != null && Number.isFinite(pctTempo);
  const hasQtd = pctQtd != null && Number.isFinite(pctQtd);

  let statusText = "Sem dados de aderência";
  let tone = "neutral";

  if (hasTempo || hasQtd) {
    if (tempoOk && qtdOk) {
      statusText = "Aderente";
      tone = "ok";
    } else if (!tempoOk && qtdOk) {
      statusText = "Intervalo não aderente";
      tone = "alert";
    } else if (tempoOk && !qtdOk) {
      statusText = "Produção não aderente";
      tone = "alert";
    } else {
      statusText = "Intervalo e produção não aderentes";
      tone = "alert";
    }
  } else {
    statusText = "Sem aderência";
  }

  return {
    hasTempo,
    hasQtd,
    tempoOk,
    qtdOk,
    pctTempo,
    pctQtd,
    statusText,
    tone,
  };
}

function W3CardAderencia({
  tempoPrevistoMinutos,
  tempoRealizadoMinutos,
  quantidadePrevista,
  quantidadeRealizada,
}) {
  const {
    hasTempo,
    hasQtd,
    tempoOk,
    qtdOk,
    pctTempo,
    pctQtd,
    statusText,
    tone,
  } = getAderenciaStatus(
    tempoPrevistoMinutos,
    tempoRealizadoMinutos,
    quantidadePrevista,
    quantidadeRealizada
  );

  const pctTempoText = !hasTempo ? "—" : `${Math.round(pctTempo)}%`;
  const pctQtdText = !hasQtd ? "—" : `${Math.round(pctQtd)}%`;
  const pctTempoBar =
    pctTempo == null || !Number.isFinite(pctTempo)
      ? 0
      : Math.max(0, Math.min(pctTempo, 150));
  const pctQtdBar =
    pctQtd == null || !Number.isFinite(pctQtd)
      ? 0
      : Math.max(0, Math.min(pctQtd, 150));
  const statusShort =
    tone === "ok" ? "ADE" : tone === "alert" ? "ALT" : "SEM";
  const tempoHeight = `${pctTempoBar}%`;
  const qtdHeight = `${pctQtdBar}%`;

  return (
    <>
      <div className={styles.pillBox}>
        <span
          className={`${styles.pill} ${styles.pillAderencia} ${styles[`pillAderencia_${tone}`]}`}
          title={statusText}
        >
          {statusShort}
        </span>
      </div>

      <div className={styles.pillBox}>
        <span
          className={`${styles.pill} ${styles.pillMetric} ${
            !hasTempo
              ? styles.pillMetric_neutral
              : tempoOk
                ? styles.pillMetric_ok
                : styles.pillMetric_alert
          }`}
          title={`Intervalo: ${pctTempoText}`}
        >
          <span className={styles.metricLabel}>IN</span>
          <span className={styles.metricValue}>{pctTempoText}</span>
          <span className={styles.metricTrack}>
            <span
              className={`${styles.metricFill} ${
                tempoOk ? styles.metricFill_ok : styles.metricFill_alert
              }`}
              style={{ height: tempoHeight }}
            />
          </span>
        </span>
      </div>

      <div className={styles.pillBox}>
        <span
          className={`${styles.pill} ${styles.pillMetric} ${
            !hasQtd
              ? styles.pillMetric_neutral
              : qtdOk
                ? styles.pillMetric_ok
                : styles.pillMetric_alert
          }`}
          title={`Produção: ${pctQtdText}`}
        >
          <span className={styles.metricLabel}>PR</span>
          <span
            className={styles.metricValue}
          >
            {pctQtdText}
          </span>
          <span className={styles.metricTrack}>
            <span
              className={`${styles.metricFill} ${
                qtdOk ? styles.metricFill_ok : styles.metricFill_alert
              }`}
              style={{ height: qtdHeight }}
            />
          </span>
        </span>
      </div>
    </>
  );
}

function ClockIcon({ size = 12, stroke = 2 }) {
  return (
    <svg
      className={styles.clockIcon}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </svg>
  );
}

export default function W3Card({ item, nowTs, onSelect }) {
  const hojeKey = todayKeyBR();
  const p = item || {};
  const id = p.codigo_unico || p.id || "";

  const dayBase =
    toKeyYYYYMMDD(p.__dataKey ?? p.data_key ?? p.dataKey ?? p.data) || hojeKey;

  const sKey = getStatusKey(p);
  const statusClass = styles[`st_${sKey}`] || "";

  const aguardandoInicio = ![
    p.primeiro_inicio,
    p.segundo_inicio,
    p.terceiro_inicio,
  ].some(hasRealStartValue);

  const orderIdx = statusOrderIndex(p);
  const tempoAlvo = toHHMM(p.tempo_do_intervalo || p.tempo_total) || "--:--";
  const tempoTotalMinutos = sumIntervalsMinutes(p);
  const tempoIntervaloPrevistoMinutos = toMinutes(p.tempo_do_intervalo);
  const quantidadePrevistaNum = toNumberOrNull(p.qtd ?? p.quantidade);
  const quantidadeRealizadaNum = toNumberOrNull(p.quantidade_real);

  return (
    <div
      className={`${styles.card} ${statusClass}`}
      style={{ order: orderIdx }}
      role="button"
      onClick={() => onSelect?.({ ...p, __dataKey: dayBase })}
      title="Abrir ficha WF-3"
      data-status={sKey}
      data-order={orderIdx}
      aria-label={`WF3 ${id || ""} ${STATUS_LABEL[sKey]}`}
    >
      <div className={styles.metaCol} aria-hidden>
        <div className={styles.metaStack}>
          <W3CardAderencia
            tempoPrevistoMinutos={tempoIntervaloPrevistoMinutos}
            tempoRealizadoMinutos={tempoTotalMinutos}
            quantidadePrevista={quantidadePrevistaNum}
            quantidadeRealizada={quantidadeRealizadaNum}
          />
        </div>

        <div className={styles.metaStack}>
          <div className={styles.pillBox}>
            <span className={`${styles.pill} ${styles.pillCode}`}>{id || "—"}</span>
          </div>

          <div className={styles.pillBox}>
            <span className={`${styles.pill} ${styles.pillStatus}`}>
              {STATUS_LABEL[sKey]}
            </span>
          </div>

          <div className={styles.pillBox}>
            <span
              className={`${styles.pill} ${styles.pillTimer}`}
              title={
                aguardandoInicio
                  ? "Tempo do intervalo (meta)"
                  : "Cronômetro de execução"
              }
            >
              <span className={styles.timerInline}>
                <ClockIcon size={12} />
                {aguardandoInicio ? (
                  <span className={styles.timerPlain}>{tempoAlvo}</span>
                ) : (
                  <span className={styles.timerPlain}>
                    <W3Timer
                      nowTs={nowTs}
                      day={dayBase}
                      tempo_total={p.tempo_total || p.tempo_do_intervalo || "00:10"}
                      tempo_do_intervalo={p.tempo_do_intervalo}
                      primeiro_inicio={p.primeiro_inicio}
                      primeiro_fim={p.primeiro_fim}
                      segundo_inicio={p.segundo_inicio}
                      segundo_fim={p.segundo_fim}
                      terceiro_inicio={p.terceiro_inicio}
                      terceiro_fim={p.terceiro_fim}
                    />
                  </span>
                )}
              </span>
            </span>
          </div>
        </div>
      </div>

      <div className={styles.cardBody}>
        <div>
          <b>Gerência:</b> {p.gerencia || "—"}
        </div>
        <div>
          <b>Coordenador:</b> {p.coordenador || "—"}
        </div>
        <div>
          <b>Atividade:</b> {p.atividade || "—"}
        </div>
        <div>
          <b>Ativo:</b> {p.ativo || "—"}
        </div>

        <div>
          <b>Prioridade:</b> {p.prioridade || p.po || "—"}
          {p.qtd ? (
            <>
              {" "}
              | <b>Quantidade:</b> {p.qtd}
            </>
          ) : null}
        </div>

        <div>
          <b>Origem:</b> {p.sbOrigem || "—"}
          <b style={{ marginLeft: 8 }}>Trab.:</b> {p.sbDeTrabalho || "—"}
          <b style={{ marginLeft: 8 }}>Encerr.:</b> {p.sbEncerramento || "—"}
        </div>

        <div>
          <b>KM:</b> {p.kmInicio || "—"}
          {p.kmFinal ? ` - ${p.kmFinal}` : ""}
        </div>

        <div>
          <b>Início:</b> {toHHMM(p.horario_do_intervalo)}{" "}
          <b style={{ marginLeft: 8 }}>Intervalo:</b> {toHHMM(p.tempo_do_intervalo)}
        </div>

        <div>
          <b>Responsável:</b> {p.responsavel || p.responsavel_pelo_aceite || "—"}
        </div>
      </div>
    </div>
  );
}
